// MSQA site forms on the phone (Construction field workspace, 2026-10-13).
// Backend: src/wcc_viewer/msqa.py + routers/msqa.py; design:
// reports/msqa_forms_built_2026-10-13.md.
//
// The monitor picks the project, starts a site visit or audit, and fills in
// one card per box: the guidance prompt, big status buttons (Compliant /
// Non-compliant / N/A, or Y / N / N/A), a comment and photos ("Take photo"
// opens the camera: <input type=file accept=image/* capture=environment>;
// "From library" picks saved photos - no capture attribute, row #8).
// The open form survives the phone reloading WRAP while the camera app is in
// front (msqaResume.ts, row #7): it reopens at the item, and says the photo
// didn't arrive.
// Works with no signal:
//   * the form lives in IndexedDB (outbox.ts MSQA_FORMS) and every change is
//     autosaved there at once and queued for the server (PUT
//     /api/msqa/forms/<id>; a newer save replaces the queued one);
//   * each photo is a field capture (form_kind msqa_photo) queued in the
//     same outbox as every other field photo - the server blurs faces and
//     plates and strips EXIF - and the item references it by its
//     client_uuid + sha256 (a small preview stays on the phone);
//   * Submit queues the content + signatures behind the photos; the server
//     waits for them (409 photos_pending -> retried) and then locks the form.
// Template engine (row #15, WISL review R1): a conditional ("if applicable")
// section has a Yes / No switch (pre-field applicable_<S>) - No puts the
// whole section to N/A and its answers aren't sent; hold-point items carry a
// badge, and submitting one non-compliant holds work until the release
// signature; an outcome can need a conditions / actions box (one action per
// line); a document_check section answers Verified (with the date, and
// optionally the project document) / Not verified / N/A per row.
// Signatures: signature_pad (vendored, pinned) on this phone - the monitor
// (the signed-in WRAP user), then the contractor (typed name + company).
// Each records the time, approximate location if permitted, the user agent
// and the SHA-256 of the form content at signing (msqaCanonical.ts, the
// same hash the server computes). Editing after signing clears signatures.
// Email this form (review #29, msqaEmail.ts): the recipients box at the top
// of the form is saved on the phone's copy (LocalForm.recipients) and sent as
// a top-level `recipients` key on the autosave / submit, never inside the
// hashed content; after submit the form's row in Submitted has the box with
// "Open in my mail app" / "Share PDF" (opened by itself after this phone's submit).
import SignaturePad from "../vendor/signature_pad";
import { familyChoice, type TemplateFamilies } from "./msqaFamilies";
import { getJson, postJson } from "../api";
import { getState } from "../state";
import { showActionToast, showToast } from "../toast";
import { fill, h, fmtDate } from "./dom";
import { MSQA_FORMS, MSQA_PHOTOS, discard, enqueue, listOutbox, newUuid, onUploaded, outboxOwner, tx } from "./outbox";
import type { OutboxItem } from "./outbox";
import { blobSha256, contentHash, signerDigest } from "./msqaCanonical";
import { addPhotosToSubmitted, downscale } from "./msqaPhotos";
import { clearResume, readResume, saveResume } from "./msqaResume";
import { emailBox } from "./msqaEmail";
import { PHONE_KEEP_DEFAULT, clearPrompt, decideClear, describeChanges, invalidRoles, latestSigned, roleLabels, rolesClearedBy, signatureChange, pushLocalRev, sigLabels, signAgainText, signSchemeFor, signaturesValidFor, signedWords, summariseChanges } from "./msqaHistory";
import type { Fingerprint, LocalRev, SigChange } from "./msqaHistory";
import type { Recipient } from "./msqaEmail";
import "../styles/msqa.css";

// ---------------------------------------------------------------------------
// Types (templates come from config/msqa_forms/*.yaml via /api/msqa/templates)
// ---------------------------------------------------------------------------

export interface MsqaField {
  key: string;
  label: string;
  type?: string;
  prefill?: string;
  required?: boolean;
  placeholder?: string;
  sublabel?: string;
  group_label?: string;
  suffix?: string;
  help?: string;
  options?: { key: string; label: string }[];
  applicable_for?: string;
  required_when?: string[];
  creates_actions?: boolean;
  min_rows?: number; // "items" (row #50): rows shown even when empty
  name_placeholder?: string;
  count_label?: string;
}
export interface MsqaItem {
  key: string;
  number: number;
  label: string;
  guidance: string[];
  adverse?: "Y" | "N";
  transcription_note?: string;
  hold_point?: boolean;
  clause?: string;
  responsible?: string;
}
export interface MsqaSection {
  key: string;
  letter?: string;
  title?: string;
  table_title?: string;
  answer: "compliance" | "yes_no" | "document_check";
  answer_label?: string;
  comment_label?: string;
  allow_na?: boolean;
  initials?: boolean;
  initials_label?: string;
  pre_fields?: MsqaField[];
  items: MsqaItem[];
  conditional?: boolean;
  hold_point?: boolean;
  submit_requires_verified?: boolean;
}
export interface MsqaOption {
  key: string;
  label: string;
  short_label?: string;
  suspension?: boolean;
  tone?: "ok" | "warn" | "bad";
  requires_no_nc?: boolean;
}
export interface MsqaSigDef {
  role: string;
  label: string;
  caption?: string;
  company_label?: string;
  position_label?: string;
  signer: "wrap_user" | "typed";
  required?: boolean;
  after_submit?: boolean;
  placement?: string;
}
export interface MsqaTemplate {
  key: string;
  version: number;
  kind?: string;
  /** Review #32: the template's family ("tiaki_wai", "wisl", "wrap"); picked per document. */
  family?: string;
  title: string;
  short_title?: string;
  description?: string;
  use_note?: string;
  header?: MsqaField[];
  resources?: { key: string; label: string; fields: MsqaField[] }[];
  sections: MsqaSection[];
  intro?: string | string[];
  intro_title?: string;
  summary?: {
    title?: string;
    outcome?: { key: string; noun?: string; options: MsqaOption[] };
    non_compliant_action?: { key: string; options: MsqaOption[] };
    fields?: MsqaField[];
    statements?: string[];
    disclaimer?: string;
    disclaimer_title?: string;
  };
  comments?: { key: string; label: string };
  signatures?: MsqaSigDef[];
  organisation?: string;
}

interface PhotoRef {
  ref: string;
  caption?: string;
  sha256?: string;
}
interface Answer {
  status?: string;
  comment?: string;
  initials?: string;
  photos?: PhotoRef[];
  date?: string;
  document_id?: string;
}
/** An "items" field's row (row #50): other plant "Hydro" 1. Strings, as every field value. */
export interface ItemRow {
  name?: string;
  count?: string;
}
type FieldValue = string | ItemRow[];
interface Content {
  header: Record<string, string>;
  resources: Record<string, Record<string, FieldValue>>;
  pre: Record<string, string>;
  answers: Record<string, Answer>;
  summary: Record<string, string>;
  signers: Record<string, { name?: string; company?: string; position?: string }>;
}
interface LocalSig {
  role: string;
  image: string;
  name?: string;
  company?: string;
  signed_at: string;
  lat?: number;
  lon?: number;
  accuracy_m?: number;
  location_status: string;
  user_agent: string;
  content_hash: string | null;
  /** Row #84: 2 = content_hash leaves the signers out and signer_digest binds this signer's own details; absent = 1. */
  hash_scheme?: number;
  signer_digest?: string | null;
}
export interface LocalForm {
  id: string;
  project_id: string;
  project_name: string;
  template: MsqaTemplate;
  content: Content;
  signatures: LocalSig[];
  status: "draft" | "queued" | "submitted" | "rejected";
  owner: string | null;
  updated: number;
  error?: string | null;
  /** Email box (review #29): undefined = nobody chose yet (the suggestions' defaults apply). Not hashed. */
  recipients?: Recipient[];
  /** Form history (row #83, msqaHistory.ts): this phone's last few versions, newest last, so undo works offline. */
  history?: LocalRev<Content, LocalSig>[];
  /** Set when a version was put back on the phone: the next draft save tells the server (audited msqa.draft_restore). */
  restoreMark?: { content_hash: string; seq: number | null };
}

interface ServerForm {
  id: string;
  template_key: string;
  short_title: string;
  title: string;
  status: string;
  visit_date: string | null;
  outcome: string | null;
  outcome_label?: string | null;
  outcome_tone?: string | null;
  nc_count: number;
  created_by: number;
  created_by_name: string | null;
  submitted_at: string | null;
  n_amendments: number;
  suspension: boolean;
  suspension_reasons?: string[];
  contractor: string | null;
}
interface Overview {
  project: { id: string; name: string };
  forms: ServerForm[];
  suspensions: { id: number; form_id: string; raised_at: string; raised_by_name: string | null }[];
  open_non_compliances: { form_id: string; item_key: string; label: string; visit_date: string | null }[];
  can_fill: boolean;
  settings: { tracked_actions: boolean; contractor_visibility: boolean };
  /** 2026-10-28: the families, opening on this project's council's default (WISL: Tiaki Wai / WWL in a Tiaki Wai council) */
  template_families?: TemplateFamilies;
  last_visit: string | null;
  days_since_last_visit: number | null;
  templates: { key: string; short_title: string; title: string; description?: string }[];
}

export interface MsqaHost {
  projectId: () => string | null;
  projects: () => { id: string; name: string; phase_label: string | null; in_construction: boolean; role: string | null }[];
  selectProject: (id: string) => void;
  fix: () => { lat: number; lon: number; accuracy: number } | null;
  heading: () => number | null;
  rerender: () => void;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const TEMPLATE_CACHE = "wrap.msqa.templates";
const FAMILY_CACHE = "wrap.msqa.templateFamilies";
const M: {
  templates: MsqaTemplate[] | null;
  families: TemplateFamilies | null;
  /** The family picked on this phone for the next new form ("all" = every family). */
  family: string | null;
  loadingTemplates: boolean;
  overview: Overview | null;
  overviewFor: string | null;
  overviewError: string | null;
  local: LocalForm[];
  editing: LocalForm | null;
  editorEl: HTMLElement | null;
  previews: Map<string, string>;
  saveTimer: number | null;
  host: MsqaHost | null;
  /** Set when the form was reopened after a reload (msqaResume.ts): where to scroll, and whether a photo was lost. */
  resumed: { item: string | null; photo_pending: boolean } | null;
  /** Review #29: forms submitted from this phone whose email box opens by itself in Submitted. */
  emailAfter: Set<string>;
  /** Submitted rows whose email box is open. */
  emailOpen: Set<string>;
  /** Row #83: the "clears the signatures?" question is open (further edits wait for it). */
  confirming: boolean;
  /** Row #83: signatures were cleared by an edit - who must sign again, and the signed version Undo goes back to. */
  cleared: { formId: string; labels: string[]; roles: string[]; rev: LocalRev<Content, LocalSig> | null } | null;
  /** The phone's history length (config/msqa_forms/_settings.yaml history.phone_keep, via /templates). */
  phoneKeep: number;
  /** The "Sign again / Undo" toast, removed once the signatures are back. */
  signAgainToast: HTMLElement | null;
} = {
  templates: null, families: null, family: null, loadingTemplates: false, overview: null, overviewFor: null, overviewError: null, local: [], editing: null,
  editorEl: null, previews: new Map(), saveTimer: null, host: null, resumed: null, emailAfter: new Set(), emailOpen: new Set(),
  confirming: false, cleared: null, phoneKeep: PHONE_KEEP_DEFAULT, signAgainToast: null,
};

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function editable(f: LocalForm): boolean {
  return f.status === "draft" || f.status === "rejected";
}

/** Remember the open form (and the item waiting for a photo) in case the phone reloads WRAP. */
function remember(f: LocalForm, item: string | null = null, photoPending = false): void {
  if (editable(f)) saveResume(store(), { form_id: f.id, project_id: f.project_id, owner: f.owner, item, photo_pending: photoPending });
}

function loadCachedTemplates(): MsqaTemplate[] | null {
  try {
    const s = localStorage.getItem(TEMPLATE_CACHE);
    return s ? (JSON.parse(s) as MsqaTemplate[]) : null;
  } catch {
    return null;
  }
}

async function ensureTemplates(): Promise<void> {
  if (M.templates || M.loadingTemplates) return;
  M.loadingTemplates = true;
  try {
    const r = await getJson<{ templates: MsqaTemplate[]; template_families?: TemplateFamilies; history?: { phone_keep?: number } }>("/api/msqa/templates");
    M.templates = r.templates;
    if (r.history?.phone_keep) M.phoneKeep = r.history.phone_keep;
    M.families = r.template_families ?? null;
    try {
      localStorage.setItem(TEMPLATE_CACHE, JSON.stringify(r.templates));
      localStorage.setItem(FAMILY_CACHE, JSON.stringify(M.families));
    } catch {
      /* storage full / blocked: online only */
    }
  } catch {
    M.templates = loadCachedTemplates();
    try {
      const f = localStorage.getItem(FAMILY_CACHE);
      M.families = f ? (JSON.parse(f) as TemplateFamilies) : null;
    } catch {
      M.families = null;
    }
  } finally {
    M.loadingTemplates = false;
    M.host?.rerender();
  }
}

async function loadOverview(projectId: string): Promise<void> {
  try {
    M.overview = await getJson<Overview>(`/api/msqa/projects/${encodeURIComponent(projectId)}`);
    M.overviewFor = projectId;
    M.overviewError = null;
  } catch (e) {
    M.overviewError = navigator.onLine ? `Couldn't load the project's forms (${(e as Error).message})` : "Offline: submitted forms show when you're back online.";
  }
  if (!M.editing) M.host?.rerender();
}

async function loadLocal(): Promise<void> {
  const all = (await tx<LocalForm[]>(MSQA_FORMS, "readonly", (s) => s.getAll())) ?? [];
  M.local = all.sort((a, b) => b.updated - a.updated);
}

async function putLocal(f: LocalForm): Promise<void> {
  f.updated = Date.now();
  await tx(MSQA_FORMS, "readwrite", (s) => s.put(JSON.parse(JSON.stringify(f))));
}

async function previewUrl(ref: string): Promise<string | null> {
  const have = M.previews.get(ref);
  if (have) return have;
  const rec = await tx<{ ref: string; blob: Blob } | undefined>(MSQA_PHOTOS, "readonly", (s) => s.get(ref));
  if (!rec?.blob) return null;
  const url = URL.createObjectURL(rec.blob);
  M.previews.set(ref, url);
  return url;
}

let uploadHooked = false;
function hookUploads(): void {
  if (uploadHooked) return;
  uploadHooked = true;
  onUploaded((rec, item: OutboxItem) => {
    const key = item.client_uuid;
    if (!key.startsWith("msqa-submit-")) return;
    const id = key.slice("msqa-submit-".length);
    void (async () => {
      await loadLocal();
      const f = M.local.find((x) => x.id === id);
      if (f) {
        f.status = "submitted";
        f.error = null;
        await putLocal(f);
      }
      showToast("MSQA form submitted - the project manager has been notified.");
      if (M.overviewFor) void loadOverview(M.overviewFor);
      M.host?.rerender();
    })();
    void rec;
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyContent(): Content {
  return { header: {}, resources: {}, pre: {}, answers: {}, summary: {}, signers: {} };
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function isAdverse(sec: MsqaSection, it: MsqaItem, a: Answer | undefined): boolean {
  const v = a?.status;
  if (sec.answer === "compliance") return v === "non_compliant";
  if (sec.answer === "document_check") return v === "not_verified";
  return !!v && v === (it.adverse ?? "N");
}

/** msqa.py section_applies: a conditional section answered No is N/A. */
function sectionApplies(sec: MsqaSection, c: Content): boolean {
  return !sec.conditional || c.pre[`applicable_${sec.key}`] !== "N";
}

function activeItems(t: MsqaTemplate, c: Content): { sec: MsqaSection; it: MsqaItem }[] {
  return t.sections.filter((s) => sectionApplies(s, c)).flatMap((sec) => sec.items.map((it) => ({ sec, it })));
}

function statusOptions(sec: MsqaSection): { key: string; label: string; cls: string }[] {
  if (sec.answer === "document_check") {
    return [
      { key: "verified", label: "Verified", cls: "ok" },
      { key: "not_verified", label: "Not verified", cls: "bad" },
      { key: "na", label: "N/A", cls: "na" },
    ];
  }
  if (sec.answer === "compliance") {
    return [
      { key: "compliant", label: "Compliant", cls: "ok" },
      { key: "non_compliant", label: "Non-compliant", cls: "bad" },
      { key: "na", label: "N/A", cls: "na" },
    ];
  }
  const opts = [{ key: "Y", label: "Y", cls: "" }, { key: "N", label: "N", cls: "" }];
  if (sec.allow_na !== false) opts.push({ key: "na", label: "N/A", cls: "na" });
  return opts;
}

/** The content as the server keeps it (template fields only) - what the
 * signatures hash. The server's _clean_content does the same filtering. */
/** pick() for resources, whose fields may be "items" rows (row #50): each row
 * as {name, count} only, as the server keeps it (msqa._clean_items). */
function pickValues(defs: MsqaField[] | undefined, src: Record<string, FieldValue> | undefined): Record<string, FieldValue> {
  const o: Record<string, FieldValue> = {};
  for (const d of defs ?? []) {
    const v = src?.[d.key];
    if (v === undefined) continue;
    o[d.key] = Array.isArray(v) ? v.map((r) => ({ name: r.name, count: r.count })) : v;
  }
  return o;
}

function hashableContent(f: LocalForm): Content {
  const t = f.template;
  const c = f.content;
  const pick = (defs: MsqaField[] | undefined, src: Record<string, string> | undefined) => {
    const o: Record<string, string> = {};
    for (const d of defs ?? []) if (src?.[d.key] !== undefined) o[d.key] = src[d.key];
    return o;
  };
  const answers: Record<string, Answer> = {};
  const pre = pick(t.sections.flatMap((s) => s.pre_fields ?? []), c.pre);
  for (const s of t.sections) {
    // a section marked not applicable sends no answers (the server drops them too); they stay on the phone
    if (s.conditional && pre[`applicable_${s.key}`] === "N") continue;
    for (const it of s.items) {
      const a = c.answers[it.key];
      if (!a) continue;
      answers[it.key] = {
        status: a.status, comment: a.comment, initials: a.initials,
        photos: (a.photos ?? []).map((p) => ({ ref: p.ref, caption: p.caption ? p.caption.slice(0, 500) : undefined, sha256: p.sha256 })),
        ...(s.answer === "document_check" ? { date: a.date, document_id: a.document_id } : {}),
      };
    }
  }
  const summary: Record<string, string> = {};
  if (t.summary?.outcome && c.summary.outcome) summary.outcome = c.summary.outcome;
  if (t.summary?.non_compliant_action && c.summary.non_compliant_action) summary.non_compliant_action = c.summary.non_compliant_action;
  Object.assign(summary, pick(t.summary?.fields, c.summary));
  if (t.comments && c.summary[t.comments.key]) summary[t.comments.key] = c.summary[t.comments.key];
  const signers: Content["signers"] = {};
  for (const sg of t.signatures ?? []) {
    const s = c.signers[sg.role];
    if (s && (s.name || s.company || s.position)) {
      signers[sg.role] = { name: s.name?.slice(0, 200), company: s.company?.slice(0, 200), position: s.position?.slice(0, 200) };
    }
  }
  return {
    header: pick(t.header, c.header),
    resources: Object.fromEntries((t.resources ?? []).map((g) => [g.key, pickValues(g.fields, c.resources[g.key])])),
    pre,
    answers, summary, signers,
  };
}

function problems(f: LocalForm): string[] {
  const t = f.template;
  const c = f.content;
  const out: string[] = [];
  for (const d of t.header ?? []) if (d.required && !(c.header[d.key] ?? "").trim()) out.push(`${d.label.replace(/:$/, "")} is required`);
  // row #50: "items" rows (other plant) - a count needs a name, and is a whole number (the server checks the same)
  for (const g of t.resources ?? []) {
    for (const d of g.fields) {
      if (d.type !== "items") continue;
      const rows = c.resources[g.key]?.[d.key];
      for (const r of Array.isArray(rows) ? rows : []) {
        const name = (r.name ?? "").trim(), count = (r.count ?? "").trim();
        if (count && !/^\d{1,5}$/.test(count)) out.push(`${d.label}: the count for ${name || "a row"} must be a whole number`);
        else if (count && !name) out.push(`${d.label}: say what the ${count} is`);
      }
    }
  }
  const nc: string[] = [];
  for (const { sec: s, it } of activeItems(t, c)) {
    const a = c.answers[it.key];
    if (!a?.status) out.push(`${it.key} ${it.label}: choose an answer (N/A is allowed)`);
    else if (isAdverse(s, it, a)) {
      nc.push(it.key);
      if (!(a.comment ?? "").trim()) out.push(`${it.key}: ${s.answer === "document_check" ? "a document not verified" : "a non-compliance"} needs a comment`);
    }
    if (s.answer === "document_check" && a?.status === "verified" && !a.date) out.push(`${it.key} ${it.label}: give the date it was verified`);
    if (s.submit_requires_verified && a?.status === "not_verified") out.push(`${it.key}: this certificate can't be issued with a document not verified`);
  }
  if (t.summary?.outcome) {
    const o = c.summary.outcome;
    const opt = t.summary.outcome.options.find((x) => x.key === o);
    if (!o) out.push(`Choose the ${t.summary.outcome.noun ?? "visit"} outcome`);
    else if ((o === "no_deficiencies" || opt?.requires_no_nc) && nc.length) {
      out.push(`The outcome says ${opt?.short_label ?? opt?.label ?? "no deficiencies"}, but ${nc.length} item(s) are non-compliant (${nc.join(", ")})`);
    } else if (o === "non_compliant" && t.summary.non_compliant_action && !c.summary.non_compliant_action) out.push("Non-compliant: choose what happens next");
  }
  for (const d of t.summary?.fields ?? []) {
    const need = d.required || (d.required_when ?? []).includes(c.summary.outcome ?? "");
    if (need && !(c.summary[d.key] ?? "").trim()) out.push(`${d.label.replace(/:$/, "")} is required${d.required_when ? " for this outcome" : ""}`);
  }
  return out;
}

function getPosition(timeoutMs = 6000): Promise<{ lat?: number; lon?: number; accuracy_m?: number; status: string }> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ status: "unavailable" });
      return;
    }
    let done = false;
    const t = window.setTimeout(() => {
      if (!done) {
        done = true;
        const f = M.host?.fix();
        resolve(f ? { lat: f.lat, lon: f.lon, accuracy_m: f.accuracy, status: "granted" } : { status: "timeout" });
      }
    }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        if (done) return;
        done = true;
        window.clearTimeout(t);
        resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy_m: p.coords.accuracy, status: "granted" });
      },
      (err) => {
        if (done) return;
        done = true;
        window.clearTimeout(t);
        resolve({ status: err.code === err.PERMISSION_DENIED ? "denied" : "unavailable" });
      },
      { enableHighAccuracy: false, maximumAge: 120000, timeout: timeoutMs },
    );
  });
}

function userName(): string {
  return getState().currentUser?.display_name ?? "";
}

// ---------------------------------------------------------------------------
// Autosave + queue
// ---------------------------------------------------------------------------

function draftBody(f: LocalForm) {
  // row #83: the signatures ride along as PENDING evidence (msqa.py keeps them on the revision only
  // when made over this content); restoreMark says this save puts back an earlier version
  return { project_id: f.project_id, template_key: f.template.key, template_version: f.template.version,
           content: hashableContent(f), client_saved_at: new Date().toISOString(), signatures: f.signatures,
           ...(f.restoreMark ? { restore: f.restoreMark } : {}),
           ...(f.recipients ? { recipients: f.recipients } : {}) };
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** Row #84: what the signatures are checked against - the whole content's
 * hash (scheme 1), the form hash without the signers (scheme 2) and each
 * signer's digest over it (msqa.py sig_valid does the same). */
async function fingerprint(f: LocalForm, content: Content = hashableContent(f)): Promise<Fingerprint> {
  const [h1, h2] = await Promise.all([1, 2].map((sc) => contentHash(f.id, f.template.key, f.template.version, content, sc)));
  const digests: Record<string, string | null> = {};
  await Promise.all(sigDefs(f).map(async (d) => { digests[d.role] = await signerDigest(h2, d.role, content.signers[d.role]); }));
  return { h1, h2, digests };
}

/** Row #83: keep this version in the phone's own history (the form's IndexedDB record). */
async function keepVersion(f: LocalForm): Promise<void> {
  const fp = await fingerprint(f);
  if (!fp.h1) return;
  f.history = pushLocalRev(f.history, { at: Date.now(), hash: fp.h1, fp, content: clone(f.content), signatures: clone(f.signatures) }, M.phoneKeep);
  await putLocal(f);
}

function scheduleSave(f: LocalForm, immediate = false): void {
  void putLocal(f); // the phone copy is saved at once
  if (M.saveTimer) window.clearTimeout(M.saveTimer);
  const go = () => {
    M.saveTimer = null;
    if (f.status !== "draft" && f.status !== "rejected") return;
    void keepVersion(f);
    void enqueue({
      client_uuid: `msqa-draft-${f.id}`, endpoint: `/api/msqa/forms/${encodeURIComponent(f.id)}`, method: "PUT", json: draftBody(f),
      meta: { form_kind: "msqa_form", label: `MSQA ${f.template.short_title ?? f.template.title} (draft)` }, photos: [], photo_headings: [],
    });
  };
  if (immediate) go();
  else M.saveTimer = window.setTimeout(go, 1500);
  setSaveNote("Saved on this phone");
}

let saveNoteEl: HTMLElement | null = null;
function setSaveNote(t: string): void {
  if (saveNoteEl) saveNoteEl.textContent = `${t} · ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function edited(f: LocalForm, change: SigChange = { kind: "content" }): void {
  const roles = rolesClearedBy(f.signatures, change);
  if (roles.length) {
    // row #83: an edit after signing clears the signatures - ask first (the edit is already on the
    // form; Cancel puts the signed version back exactly). Row #84: a signer's own name / company /
    // position clears only that signer's signature.
    if (!M.confirming) void confirmClear(f, roles, change);
    return;
  }
  f.restoreMark = undefined;
  scheduleSave(f);
  updateNcNote(f);
  renderSubmit();
}

function sigDefs(f: LocalForm): MsqaSigDef[] {
  return (f.template.signatures ?? []).filter((s) => !s.after_submit);
}

async function confirmClear(f: LocalForm, roles: string[], change: SigChange): Promise<void> {
  M.confirming = true;
  const go = await askModal(clearPrompt(roleLabels(sigDefs(f), roles)), "Change it anyway", "Cancel");
  M.confirming = false;
  if (M.editing !== f) return;
  const d = decideClear(sigDefs(f), f.signatures, f.history, go, roles);
  if (d.kind === "put_back") await putBack(f, d.rev, "cancel");
  if (d.kind !== "clear") return;
  clearRoles(f, d.roles, d.labels, d.undo);
  edited(f, change);
}

/** Take these signatures off (an edit cleared them): the form says who signs again, with Undo. */
function clearRoles(f: LocalForm, roles: string[], labels: string[], undo: LocalRev<Content, LocalSig> | null): void {
  f.signatures = f.signatures.filter((s) => !roles.includes(s.role));
  M.cleared = { formId: f.id, labels, roles, rev: undo };
  showSignAgain(f);
  renderSignatures();
}

/** The cleared signatures still to sign again (row #84: may be some of them, not all). */
function stillCleared(f: LocalForm): NonNullable<typeof M.cleared> | null {
  const c = M.cleared;
  return c && c.formId === f.id && c.roles.some((r) => !f.signatures.some((s) => s.role === r)) ? c : null;
}

/** The signatures are back (signed again, undone or restored): the "Sign again" prompts go. */
function settleCleared(f: LocalForm, force = true): void {
  if (!force && stillCleared(f)) return;
  if (M.cleared?.formId === f.id) M.cleared = null;
  M.signAgainToast?.remove();
  M.signAgainToast = null;
}

function scrollToSignatures(): void {
  sigHost?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function showSignAgain(f: LocalForm): void {
  const c = M.cleared;
  if (!c || c.formId !== f.id) return;
  M.signAgainToast?.remove();
  M.signAgainToast = showActionToast({
    title: signAgainText(c.labels),
    autoDismissMs: 20_000,
    actions: [
      { label: "Go to signatures", onClick: scrollToSignatures },
      ...(c.rev ? [{ label: "Undo", onClick: () => { if (M.editing === f && c.rev) void putBack(f, c.rev, "undo"); } }] : []),
    ],
  });
}

/** Rebuild the open editor where it was (the restored values are drawn fresh). */
function rebuildEditor(): void {
  const old = M.editorEl;
  const keep: [Element, number][] = [];
  for (let el = old?.parentElement ?? null; el; el = el.parentElement) if (el.scrollTop) keep.push([el, el.scrollTop]);
  const doc = document.scrollingElement;
  const docTop = doc?.scrollTop ?? 0;
  M.editorEl = null;
  M.host?.rerender();
  window.requestAnimationFrame(() => {
    for (const [el, top] of keep) el.scrollTop = top;
    if (doc) doc.scrollTop = docTop;
  });
}

/** Put a version back on the phone: its content, and its signatures only
 * when the content still hashes to exactly what they signed (checked here,
 * and again by the server on the next save / at submit). */
async function putBack(f: LocalForm, rev: { content: Content; signatures: LocalSig[]; hash: string; seq?: number | null },
                       how: "cancel" | "undo" | "history"): Promise<boolean> {
  f.content = { ...emptyContent(), ...clone(rev.content) };
  const fp = await fingerprint(f);
  const hash = fp.h1;
  const ok = hash === rev.hash && signaturesValidFor(rev.signatures, fp);
  f.signatures = ok ? clone(rev.signatures) : [];
  if (ok) settleCleared(f);
  if (how !== "cancel" && hash) {
    f.restoreMark = { content_hash: hash, seq: rev.seq ?? null };
    scheduleSave(f, true);
  } else {
    await putLocal(f);
  }
  rebuildEditor();
  if (how !== "cancel") {
    showToast(ok ? `Restored the signed version - ${sigLabels(sigDefs(f), f.signatures).join(", ")} signature${f.signatures.length === 1 ? "" : "s"} kept.`
      : rev.signatures.length ? "Restored that version, but its signatures don't match its content, so they were not put back. Sign again."
        : "Restored that version.");
  }
  return ok;
}

let ncNoteEl: HTMLElement | null = null;
function ncCount(f: LocalForm): number {
  return activeItems(f.template, f.content).filter(({ sec, it }) => isAdverse(sec, it, f.content.answers[it.key])).length;
}
function updateNcNote(f: LocalForm): void {
  if (!ncNoteEl) return;
  const n = ncCount(f);
  const act = activeItems(f.template, f.content);
  const answered = act.filter(({ it }) => f.content.answers[it.key]?.status).length;
  const total = act.length;
  ncNoteEl.textContent = `${answered} of ${total} items answered · ${n} non-compliant.`;
  ncNoteEl.classList.toggle("msqa-ncnote-bad", n > 0);
}

// ---------------------------------------------------------------------------
// Starting a form
// ---------------------------------------------------------------------------

async function startForm(t: MsqaTemplate, projectId: string, projectName: string): Promise<void> {
  const f: LocalForm = {
    id: newUuid(), project_id: projectId, project_name: projectName, template: t, content: emptyContent(), signatures: [],
    status: "draft", owner: outboxOwner(), updated: Date.now(),
  };
  // header pre-fill: from the server (contract #, awarded contractor, visit # today, project streets), else offline defaults
  let pre: { header: Record<string, string>; signature_defaults?: Record<string, { company?: string }> } | null = null;
  try {
    pre = await getJson(`/api/msqa/projects/${encodeURIComponent(projectId)}/prefill?template=${encodeURIComponent(t.key)}`);
  } catch {
    pre = null;
  }
  const offline: Record<string, string> = { project_name: projectName, contract_number: "", user_name: userName(), today: today(), now: nowHHMM() };
  for (const d of t.header ?? []) {
    const v = pre?.header?.[d.key] ?? (d.prefill ? offline[d.prefill] : undefined);
    if (v) f.content.header[d.key] = String(v);
  }
  for (const [role, v] of Object.entries(pre?.signature_defaults ?? {})) if (v.company) f.content.signers[role] = { company: v.company };
  if (!pre && t.header?.some((d) => d.key === "location")) {
    const fix = M.host?.fix();
    if (fix) f.content.header.location = `${fix.lat.toFixed(5)}, ${fix.lon.toFixed(5)}`;
  }
  await putLocal(f);
  scheduleSave(f, true);
  openEditor(f);
}

function openEditor(f: LocalForm): void {
  M.editing = f;
  M.editorEl = null;
  M.resumed = null;
  if (M.cleared?.formId !== f.id) M.cleared = null;
  // a form signed before history existed: keep its signed state now, so Cancel / Undo have it
  if (editable(f) && f.signatures.length && !latestSigned(f.history)) void keepVersion(f);
  remember(f);
  M.host?.rerender();
}

function closeEditor(): void {
  if (M.editing && (M.editing.status === "draft" || M.editing.status === "rejected")) scheduleSave(M.editing, true);
  M.editing = null;
  M.editorEl = null;
  M.resumed = null;
  M.signAgainToast?.remove();
  M.signAgainToast = null;
  clearResume(store());
  void loadLocal().then(() => M.host?.rerender());
}

/** Field app start: the form that was open when the phone reloaded WRAP
 * (e.g. while the camera app was in front). Opens it and returns its
 * project, or null. */
export async function resumeMsqa(): Promise<string | null> {
  const r = readResume(store(), outboxOwner());
  if (!r) return null;
  await loadLocal().catch(() => undefined);
  const f = M.local.find((x) => x.id === r.form_id && x.project_id === r.project_id && x.owner === outboxOwner());
  if (!f || !editable(f)) {
    clearResume(store());
    return null;
  }
  M.editing = f;
  M.editorEl = null;
  M.resumed = { item: r.item, photo_pending: r.photo_pending };
  remember(f, r.item, false);
  return f.project_id;
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

let sigHost: HTMLElement | null = null;
let submitHost: HTMLElement | null = null;

function input(type: string, value: string | undefined, on: (v: string) => void, attrs: Record<string, string> = {}): HTMLInputElement {
  const el = h("input", { type, class: "fa-input", value: value ?? "", ...attrs });
  if (type === "number") el.setAttribute("inputmode", "numeric");
  el.addEventListener("input", () => on(el.value));
  // date / time pickers: some phone browsers report the pick only as `change` (row #9)
  if (type === "date" || type === "time") el.addEventListener("change", () => on(el.value));
  return el;
}

let fieldSeq = 0;

function fieldEl(d: MsqaField, src: Record<string, string>, f: LocalForm, onChange?: () => void): HTMLElement {
  const set = (v: string) => {
    if (v) src[d.key] = v;
    else delete src[d.key];
    edited(f);
    onChange?.();
  };
  let control: HTMLElement;
  if (d.type === "yes_no" || (d.type === "choice" && d.options?.length)) {
    const opts = d.type === "yes_no" ? [{ key: "Y", label: "Yes" }, { key: "N", label: d.applicable_for ? "No - N/A" : "No" }] : d.options!;
    control = h("div", { class: "msqa-seg" });
    const draw = () => fill(control, ...opts.map((o) =>
      h("button", { type: "button", class: `msqa-segbtn${src[d.key] === o.key ? " on" : ""}`, onclick: () => { set(src[d.key] === o.key ? "" : o.key); draw(); } },
        o.label)));
    draw();
  } else if (d.type === "textarea") {
    const ta = h("textarea", { class: "fa-input", rows: 3 });
    ta.value = src[d.key] ?? "";
    ta.addEventListener("input", () => set(ta.value));
    control = ta;
  } else {
    const typ = d.type === "integer" || d.type === "number" ? "number" : d.type === "date" ? "date" : d.type === "time" ? "time" : "text";
    control = input(typ, src[d.key], set, d.placeholder ? { placeholder: d.placeholder } : {});
  }
  const label = `${d.group_label ? `${d.group_label} ` : ""}${(d.label || d.placeholder || d.key).replace(/:$/, "")}${d.required ? " *" : ""}`
    + (d.required_when?.length ? ` (required if ${d.required_when.map((k) => f.template.summary?.outcome?.options.find((o) => o.key === k)?.short_label ?? k).join(" / ")})` : "");
  if (d.type === "time") {
    // Row #9: a time can always be set without the phone's picker ("Now" /
    // "Clear"), and the control is not wrapped in a <label> (a tap on a
    // label is re-sent to the input as a second click).
    const inp = control as HTMLInputElement;
    const id = `msqa-f${++fieldSeq}`;
    inp.id = id;
    const now = h("button", { type: "button", class: "fa-btn msqa-now", onclick: () => { inp.value = nowHHMM(); set(inp.value); } }, "Now");
    const clear = h("button", { type: "button", class: "fa-link msqa-now", onclick: () => { inp.value = ""; set(""); } }, "Clear");
    return h("div", { class: "fa-field msqa-field" }, h("label", { for: id }, label), h("div", { class: "msqa-timerow" }, inp, now, clear),
      d.sublabel ? h("small", { class: "fa-muted" }, d.sublabel) : null, d.help ? h("small", { class: "fa-muted" }, d.help) : null);
  }
  return h("label", { class: "fa-field msqa-field" }, h("span", {}, label), control,
    d.sublabel ? h("small", { class: "fa-muted" }, d.sublabel) : null, d.help ? h("small", { class: "fa-muted" }, d.help) : null);
}

/** An "items" field (row #50, Connor review #21): rows of a name and a count,
 * e.g. other plant "Hydro" 1, "CCTV van" 1. + adds a row, x removes one;
 * min_rows rows are always shown. Empty rows aren't sent (normalise drops them). */
function itemsEl(d: MsqaField, src: Record<string, FieldValue>, f: LocalForm): HTMLElement {
  const rows = (): ItemRow[] => {
    const v = src[d.key];
    if (!Array.isArray(v)) src[d.key] = [];
    return src[d.key] as ItemRow[];
  };
  const list = h("div", { class: "msqa-items-rows" });
  const changed = () => edited(f);
  const draw = () => {
    const r = rows();
    while (r.length < (d.min_rows ?? 1)) r.push({});
    fill(list, ...r.map((row, i) => {
      const name = h("input", { type: "text", class: "fa-input msqa-item-name", maxlength: 200, value: row.name ?? "",
        placeholder: d.name_placeholder ?? "Name", "aria-label": `${d.label} ${i + 1}: name` }) as HTMLInputElement;
      const count = h("input", { type: "number", class: "fa-input msqa-item-count", min: "0", step: "1", inputmode: "numeric", value: row.count ?? "",
        placeholder: d.count_label ?? "No.", "aria-label": `${d.label} ${i + 1}: count` }) as HTMLInputElement;
      name.addEventListener("input", () => { row.name = name.value || undefined; changed(); });
      count.addEventListener("input", () => { row.count = count.value || undefined; changed(); });
      const x = h("button", { type: "button", class: "fa-link msqa-item-x", "aria-label": `Remove ${d.label} ${i + 1}`,
        onclick: () => { r.splice(i, 1); changed(); draw(); } }, "✕");
      return h("div", { class: "msqa-item-row" }, name, count, x);
    }));
  };
  draw();
  const add = h("button", { type: "button", class: "fa-btn msqa-item-add", onclick: () => {
    const r = rows();
    if (r.length >= 20) return showToast("20 rows at most.");
    r.push({});
    draw();
    (list.lastElementChild?.querySelector("input") as HTMLInputElement | null)?.focus();
  } }, `+ Add ${d.label.toLowerCase()}`);
  return h("div", { class: "fa-field msqa-field msqa-items-field" }, h("span", {}, d.label), list, add,
    d.help ? h("small", { class: "fa-muted" }, d.help) : null);
}

function photoStrip(f: LocalForm, it: MsqaItem, a: Answer, redraw: () => void): HTMLElement {
  const strip = h("div", { class: "msqa-photos" });
  (a.photos ?? []).forEach((p, i) => {
    const img = h("img", { alt: `${it.key} photo ${i + 1}`, class: "msqa-thumb" });
    void previewUrl(p.ref).then((u) => { if (u) img.src = u; });
    const cap = h("input", { type: "text", class: "fa-input msqa-caption", placeholder: "Caption (optional)", value: p.caption ?? "", maxlength: 500 });
    cap.addEventListener("input", () => { p.caption = cap.value || undefined; edited(f); });
    strip.append(h("figure", { class: "msqa-photo" },
      img,
      h("figcaption", {}, h("strong", {}, `${it.key}.${i + 1}`), cap),
      h("button", { type: "button", class: "fa-x", title: "Remove this photo", onclick: () => {
        if (!confirm("Remove this photo from the item? (It stays in the project's field photos.)")) return;
        a.photos = (a.photos ?? []).filter((x) => x.ref !== p.ref);
        edited(f);
        redraw();
      } }, "✕")));
  });
  return strip;
}

async function addPhotos(f: LocalForm, it: MsqaItem, a: Answer, files: File[], redraw: () => void): Promise<void> {
  const fix = M.host?.fix() ?? null;
  for (const file of files) {
    const blob = file.size > 2.5 * 1024 * 1024 ? await downscale(file) : file;
    const ref = newUuid();
    const sha = await blobSha256(blob);
    let preview: Blob = blob;
    try {
      preview = await downscale(new File([blob], "p.jpg", { type: "image/jpeg" }), 480);
    } catch {
      preview = blob;
    }
    await tx(MSQA_PHOTOS, "readwrite", (s) => s.put({ ref, blob: preview, form_id: f.id, created: Date.now() }));
    await enqueue({
      client_uuid: ref,
      meta: {
        client_uuid: ref, form_kind: "msqa_photo", workspace: "construction", project_id: f.project_id,
        captured_at: new Date().toISOString(), lat: fix?.lat ?? null, lon: fix?.lon ?? null, accuracy_m: fix?.accuracy ?? null,
        heading_deg: M.host?.heading() ?? null, heading_source: M.host?.heading() == null ? "none" : "manual",
        device: navigator.userAgent.slice(0, 300), notes: `MSQA ${f.template.short_title ?? f.template.title} ${it.key} ${it.label}`.slice(0, 400),
        form: { msqa_form: f.id, item_key: it.key },
      },
      photos: [blob], photo_headings: [M.host?.heading() ?? null],
    });
    a.photos = [...(a.photos ?? []), { ref, sha256: sha ?? undefined }];
  }
  edited(f);
  redraw();
}

/** The page survived the hand-off (the monitor cancelled, or the browser
 * didn't fire `cancel`): once WRAP is visible again and no photo arrived,
 * stop flagging the photo as pending, so a later reload doesn't claim one
 * was lost. */
function armPhotoWatch(f: LocalForm, item: string): void {
  const onVis = () => {
    if (document.visibilityState !== "visible") return;
    document.removeEventListener("visibilitychange", onVis);
    window.setTimeout(() => {
      const r = readResume(store(), f.owner);
      if (M.editing === f && r?.form_id === f.id && r.photo_pending) remember(f, item, false);
    }, 4000);
  };
  document.addEventListener("visibilitychange", onVis);
}

interface ProjectDoc {
  id: number;
  filename: string | null;
  url: string | null;
  version_label: string;
}
const docCache = new Map<string, ProjectDoc[] | null>();
/** The project's documents, for a document check row's optional link
 * (online; offline the row just isn't linked). Fetched once per project. */
function projectDocs(projectId: string): ProjectDoc[] | null {
  if (!docCache.has(projectId)) {
    docCache.set(projectId, null);
    void getJson<ProjectDoc[]>(`/api/projects/${encodeURIComponent(projectId)}/project-documents`)
      .then((r) => {
        docCache.set(projectId, r);
        M.editorEl?.querySelectorAll<HTMLElement>(".msqa-docrow").forEach((el) => el.closest(".msqa-card")?.dispatchEvent(new Event("msqa-redraw")));
      })
      .catch(() => undefined);
  }
  return docCache.get(projectId) ?? null;
}

function itemCard(f: LocalForm, sec: MsqaSection, it: MsqaItem): HTMLElement {
  const card = h("section", { class: "msqa-card", id: `msqa-${it.key}` });
  const draw = () => {
    const a: Answer = f.content.answers[it.key] ?? (f.content.answers[it.key] = {});
    const bad = isAdverse(sec, it, a);
    card.classList.toggle("bad", bad);
    card.classList.toggle("done", !!a.status && (!bad || !!(a.comment ?? "").trim()) && (a.status !== "verified" || sec.answer !== "document_check" || !!a.date));
    const picker = (camera: boolean) => {
      const el = h("input", { type: "file", accept: "image/*", capture: camera ? "environment" : undefined, multiple: true, class: "fa-file" });
      // the camera app / photo picker takes over the screen: if the phone reloads WRAP meanwhile, reopen here (row #7)
      el.addEventListener("click", () => { remember(f, it.key, true); armPhotoWatch(f, it.key); });
      el.addEventListener("cancel", () => remember(f, it.key, false));
      el.addEventListener("change", () => {
        const files = Array.from(el.files ?? []);
        el.value = "";
        remember(f, it.key, false);
        if (files.length) void addPhotos(f, it, a, files, draw);
      });
      return el;
    };
    const com = h("textarea", { class: `fa-input${bad && !(a.comment ?? "").trim() ? " msqa-need" : ""}`, rows: 2, maxlength: 4000,
      placeholder: bad ? "Required: what's wrong, what was agreed" : (sec.comment_label ?? "Comments") });
    com.value = a.comment ?? "";
    com.addEventListener("input", () => {
      a.comment = com.value || undefined;
      com.classList.toggle("msqa-need", bad && !com.value.trim());
      card.classList.toggle("done", !!a.status && (!bad || !!com.value.trim()));
      edited(f);
    });
    const doc = sec.answer === "document_check";
    const meta = doc ? [it.clause ? `Clause ${it.clause}` : "", it.responsible ? `Responsibility: ${it.responsible}` : ""].filter(Boolean).join(" · ") : "";
    let docRow: HTMLElement | null = null;
    if (doc) {
      const date = input("date", a.date, (v) => { a.date = v || undefined; edited(f); card.classList.toggle("done", !!a.status && (a.status !== "verified" || !!a.date)); });
      const docs = projectDocs(f.project_id);
      const sel = h("select", { class: "fa-input" }, h("option", { value: "" }, docs ? "No document linked" : "Project documents load with signal"),
        ...(docs ?? []).map((d) => h("option", { value: String(d.id) }, `${d.filename ?? d.url ?? "document"} (v${d.version_label})`)));
      if (a.document_id && !(docs ?? []).some((d) => String(d.id) === a.document_id)) sel.append(h("option", { value: a.document_id }, `Document #${a.document_id}`));
      sel.value = a.document_id ?? "";
      sel.addEventListener("change", () => { a.document_id = sel.value || undefined; edited(f); });
      docRow = h("div", { class: "msqa-docrow" },
        h("label", { class: "fa-field" }, h("span", {}, a.status === "verified" ? "Verified on *" : "Date"), date),
        h("label", { class: "fa-field" }, h("span", {}, "Project document (optional)"), sel));
    }
    fill(card,
      h("header", { class: "msqa-card-head" }, h("span", { class: "msqa-no" }, it.key), h("strong", {}, it.label),
        it.hold_point ? h("span", { class: "msqa-hold", title: "Hold point: a non-compliance here holds work until it is released" }, "HOLD POINT") : null),
      meta ? h("p", { class: "fa-muted msqa-docmeta" }, meta) : null,
      it.guidance.length ? h("ul", { class: "msqa-guidance" }, ...it.guidance.map((g) => h("li", {}, g))) : null,
      h("div", { class: "msqa-status" },
        ...statusOptions(sec).map((o) => h("button", {
          type: "button", class: `msqa-sbtn ${o.cls}${a.status === o.key ? " on" : ""}`, "aria-pressed": a.status === o.key ? "true" : "false",
          onclick: () => {
            a.status = a.status === o.key ? undefined : o.key;
            if (doc && a.status === "verified" && !a.date) a.date = today();
            edited(f);
            draw();
            if (isAdverse(sec, it, a) && it.hold_point) showToast("Hold point not met: submitting this holds work until the release signature. Add a comment, and a photo if you can.");
            else if (isAdverse(sec, it, a) && !(a.photos ?? []).length) showToast(doc ? "Not verified: say what's missing." : "Non-compliant: add a comment, and a photo if you can.");
          },
        }, o.label))),
      docRow,
      com,
      sec.initials ? h("label", { class: "fa-field msqa-initials" }, h("span", {}, sec.initials_label ?? "Initial"),
        input("text", a.initials, (v) => { a.initials = v || undefined; edited(f); }, { maxlength: "12", autocapitalize: "characters" })) : null,
      photoStrip(f, it, a, draw),
      h("div", { class: "msqa-photobtns" },
        h("label", { class: `fa-btn msqa-addphoto${bad && !(a.photos ?? []).length ? " prompt" : ""}` }, "📷 Take photo", picker(true)),
        h("label", { class: "fa-btn msqa-addphoto msqa-library" }, "🖼 From library", picker(false))),
    );
  };
  draw();
  card.addEventListener("msqa-redraw", draw);
  return card;
}

function renderSignatures(): void {
  const f = M.editing;
  if (!f || !sigHost) return;
  const defs = (f.template.signatures ?? []).filter((s) => !s.after_submit);
  fill(sigHost, h("h3", {}, "Signatures"),
    h("p", { class: "fa-muted" }, "Sign at the end: the signature covers the form as it is now. The contractor types their name and company on the signature pad."),
    ...defs.map((d) => {
      const got = f.signatures.find((s) => s.role === d.role);
      const sg = f.content.signers[d.role] ?? (f.content.signers[d.role] = {});
      const typed = d.signer === "typed";
      // row #84: a signer's own details belong to their signature - changing them clears only theirs
      const mine: SigChange = { kind: "signer", role: d.role };
      const who = [sg.name, sg.company].filter(Boolean).join(" · ");
      return h("div", { class: `msqa-sig${got ? " signed" : ""}`, "data-role": d.role },
        h("strong", {}, `${d.label.replace(/:$/, "")}${d.caption ? ` ${d.caption}` : ""}${d.required ? " *" : " (optional)"}`),
        typed ? (who ? h("p", { class: "msqa-sig-who" }, who) : null) : h("p", { class: "fa-muted" }, `Signing as ${userName()} (your WRAP login)`),
        d.position_label ? h("label", { class: "fa-field" }, h("span", {}, d.position_label.replace(/:$/, "")),
          input("text", sg.position, (v) => { sg.position = v || undefined; edited(f, mine); }, { autocomplete: "organization-title" })) : null,
        !typed && d.company_label ? h("label", { class: "fa-field" }, h("span", {}, d.company_label),
          input("text", sg.company, (v) => { sg.company = v || undefined; edited(f, mine); }, { autocomplete: "organization" })) : null,
        got ? h("div", { class: "msqa-sig-done" }, h("img", { src: got.image, alt: "signature" }),
          h("small", {}, `Signed ${fmtDate(got.signed_at)}${got.lat != null ? " · location recorded" : ` · location ${got.location_status}`}${got.content_hash ? ` · hash ${got.content_hash.slice(0, 10)}…` : ""}`),
          typed ? h("button", { type: "button", class: "fa-link", onclick: () => void changeTypedDetails(f, d) }, "Change name or company") : null,
          h("button", { type: "button", class: "fa-link", onclick: () => { f.signatures = f.signatures.filter((s) => s.role !== d.role); scheduleSave(f); renderSignatures(); } }, "Clear signature"))
          : h("button", { type: "button", class: "fa-btn fa-big", onclick: () => void sign(f, d) }, typed ? "Hand the phone to the contractor to sign" : "Sign"),
      );
    }));
  renderSubmit();
}

/** A time, picked (row #47): the phone's time picker, with Now (preset) and
 * Clear. Resolves "HH:MM", "" (cleared) or null (cancelled). */
function timeModal(title: string): Promise<string | null> {
  return new Promise((resolve) => {
    const inp = h("input", { type: "time", class: "fa-input", value: nowHHMM(), "aria-label": title }) as HTMLInputElement;
    const overlay = h("div", { class: "msqa-modal", role: "dialog", "aria-label": title });
    const done = (v: string | null) => {
      overlay.remove();
      resolve(v);
    };
    overlay.append(h("div", { class: "msqa-modal-box" },
      h("strong", {}, title), inp,
      h("div", { class: "msqa-modal-actions" },
        h("button", { type: "button", class: "fa-btn", onclick: () => { inp.value = nowHHMM(); } }, "Now"),
        h("button", { type: "button", class: "fa-link", onclick: () => { inp.value = ""; } }, "Clear"),
        h("button", { type: "button", class: "fa-link", onclick: () => done(null) }, "Cancel"),
        h("button", { type: "button", class: "fa-btn fa-primary", onclick: () => done(inp.value) }, "Done"))));
    (document.getElementById("field-app") ?? document.body).append(overlay);
  });
}

/** A yes / no question whose safe answer is the default (row #83): Cancel
 * has the focus, and Escape or a tap outside the box also cancels. */
function askModal(question: string, yes: string, no: string): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = h("div", { class: "msqa-modal msqa-ask", role: "alertdialog", "aria-modal": "true", "aria-label": question });
    const done = (v: boolean) => {
      document.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        done(false);
      }
    };
    const cancel = h("button", { type: "button", class: "fa-btn fa-big fa-primary msqa-ask-cancel", onclick: () => done(false) }, no);
    overlay.append(h("div", { class: "msqa-modal-box" },
      h("p", { class: "msqa-ask-q" }, question),
      h("div", { class: "msqa-ask-actions" },
        cancel,
        h("button", { type: "button", class: "fa-btn msqa-ask-yes", onclick: () => done(true) }, yes))));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) done(false); });
    document.addEventListener("keydown", onKey, true);
    (document.getElementById("field-app") ?? document.body).append(overlay);
    window.setTimeout(() => cancel.focus(), 0);
  });
}

/** The signature pad. With `ask` (a typed signer, row #84) it asks for the
 * signer's name and company first, and accepts the signature only when both
 * are filled in; the values are written back into `ask` on Done. */
function signatureModal(title: string, note: string, ask?: { name: string; company: string }): Promise<string | null> {
  return new Promise((resolve) => {
    const canvas = h("canvas", { class: "msqa-pad" });
    const nameIn = ask ? input("text", ask.name, () => {}, { autocomplete: "name", "aria-required": "true" }) : null;
    const compIn = ask ? input("text", ask.company, () => {}, { autocomplete: "organization", "aria-required": "true" }) : null;
    const details = ask ? h("div", { class: "msqa-pad-details" },
      h("label", { class: "fa-field" }, h("span", {}, "Your name *"), nameIn!),
      h("label", { class: "fa-field" }, h("span", {}, "Your company *"), compIn!)) : null;
    const overlay = h("div", { class: "msqa-modal", role: "dialog", "aria-label": title });
    let pad: SignaturePad | null = null;
    const done = (v: string | null) => {
      pad?.off();
      overlay.remove();
      window.removeEventListener("resize", size);
      resolve(v);
    };
    const size = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const data = pad?.toData();
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")!.scale(ratio, ratio);
      pad?.clear();
      if (data) pad?.fromData(data);
    };
    overlay.append(h("div", { class: "msqa-modal-box" },
      h("strong", {}, title), h("p", { class: "fa-muted" }, note), details, canvas,
      h("div", { class: "msqa-modal-actions" },
        h("button", { type: "button", class: "fa-btn", onclick: () => pad?.clear() }, "Clear"),
        h("button", { type: "button", class: "fa-link", onclick: () => done(null) }, "Cancel"),
        h("button", { type: "button", class: "fa-btn fa-primary", onclick: () => {
          if (ask && (!nameIn!.value.trim() || !compIn!.value.trim())) {
            showToast("Type your name and company first.");
            (nameIn!.value.trim() ? compIn! : nameIn!).focus();
            return;
          }
          if (!pad || pad.isEmpty()) {
            showToast("Sign in the box first.");
            return;
          }
          if (ask) {
            ask.name = nameIn!.value.trim().slice(0, 200);
            ask.company = compIn!.value.trim().slice(0, 200);
          }
          done(pad.toDataURL("image/png"));
        } }, "Done"))));
    (document.getElementById("field-app") ?? document.body).append(overlay);
    window.setTimeout(() => {
      pad = new SignaturePad(canvas, { penColor: "#0d1b4c", minWidth: 0.8, maxWidth: 2.6 });
      size();
      window.addEventListener("resize", size);
    }, 30);
  });
}

/** "Change name or company" on a typed signer who has signed (row #84): it
 * clears only their own signature (a scheme-1 draft: everyone's), so ask
 * first, then the pad asks for the details again. Cancel on the pad keeps
 * the signature as it was. */
async function changeTypedDetails(f: LocalForm, d: MsqaSigDef): Promise<void> {
  const roles = rolesClearedBy(f.signatures, { kind: "signer", role: d.role });
  if (roles.length && !(await askModal(clearPrompt(roleLabels(sigDefs(f), roles)), "Change it anyway", "Cancel"))) return;
  await sign(f, d, roles);
}

async function sign(f: LocalForm, d: MsqaSigDef, asked: string[] = []): Promise<void> {
  const sg = f.content.signers[d.role] ?? (f.content.signers[d.role] = {});
  const typed = d.signer === "typed";
  const errs = problems(f);
  if (errs.length) {
    showToast(`Finish the form before signing: ${errs[0]}${errs.length > 1 ? ` (+${errs.length - 1} more)` : ""}`);
    return;
  }
  // a typed signer gives their name and company on the pad itself, before it takes the signature
  const ask = typed ? { name: sg.name ?? "", company: sg.company ?? "" } : undefined;
  const who = typed ? `The ${d.label.replace(/:$/, "").toLowerCase()}` : userName();
  const image = await signatureModal(`${d.label.replace(/:$/, "")} signature`, `${who} - signing confirms the form as completed${d.caption ? ` ${d.caption}` : ""}.`, ask);
  if (!image) return;
  if (ask && (ask.name !== (sg.name ?? "") || ask.company !== (sg.company ?? ""))) {
    // their details changed: under scheme 2 that touches only their own signature; a scheme-1 draft's
    // other signatures covered them too, so those go (asked here unless "Change name or company" asked)
    const others = f.signatures.filter((s) => s.role !== d.role);
    const hit = rolesClearedBy(others, { kind: "signer", role: d.role }).filter((r) => r !== d.role);
    if (hit.length && !hit.every((r) => asked.includes(r))
        && !(await askModal(clearPrompt(roleLabels(sigDefs(f), hit)), "Change it anyway", "Cancel"))) return;
    if (hit.length) clearRoles(f, hit, roleLabels(sigDefs(f), hit), latestSigned(f.history));
    sg.name = ask.name;
    sg.company = ask.company;
  }
  const remaining = f.signatures.filter((s) => s.role !== d.role);
  const scheme = signSchemeFor(remaining);
  const [pos, fp] = await Promise.all([getPosition(), fingerprint(f)]);
  f.signatures = remaining;
  f.signatures.push({
    role: d.role, image, name: typed ? sg.name : undefined, company: sg.company, signed_at: new Date().toISOString(),
    lat: pos.lat, lon: pos.lon, accuracy_m: pos.accuracy_m, location_status: pos.status, user_agent: navigator.userAgent.slice(0, 400),
    ...(scheme === 2 ? { content_hash: fp.h2 ?? null, hash_scheme: 2, signer_digest: fp.digests?.[d.role] ?? null } : { content_hash: fp.h1 }),
  });
  settleCleared(f, false);
  // row #83: the signed state goes into the phone's history and to the server at once (pending evidence)
  await keepVersion(f);
  scheduleSave(f, true);
  renderSignatures();
}

function renderSubmit(): void {
  const f = M.editing;
  if (!f || !submitHost) return;
  const errs = problems(f);
  const need = (f.template.signatures ?? []).filter((s) => s.required && !s.after_submit && !f.signatures.some((x) => x.role === s.role));
  // row #83: signatures cleared by an edit - say plainly what's needed, and offer the way back
  const cl = stillCleared(f);
  fill(submitHost,
    cl ? h("div", { class: "msqa-signagain", role: "status" },
      h("p", {}, h("strong", {}, signAgainText(cl.labels))),
      h("div", { class: "msqa-signagain-actions" },
        h("button", { type: "button", class: "fa-btn", onclick: scrollToSignatures }, "Go to signatures"),
        cl.rev ? h("button", { type: "button", class: "fa-btn msqa-undo", onclick: () => { if (cl.rev) void putBack(f, cl.rev, "undo"); } }, "Undo") : null)) : null,
    errs.length ? h("details", { class: "msqa-todo" }, h("summary", {}, `${errs.length} thing(s) to finish`), h("ul", {}, ...errs.slice(0, 30).map((e) => h("li", {}, e)))) : null,
    f.status === "rejected" && f.error ? h("p", { class: "fa-warn" }, `The server refused the last submit: ${f.error}`) : null,
    h("button", { type: "button", class: "fa-btn fa-big fa-primary", disabled: errs.length > 0 || need.length > 0,
      onclick: () => void submit(f) }, need.length ? `Needs: ${need.map((s) => s.label.replace(/:$/, "")).join(", ")} signature` : "Submit (locks the form)"),
    h("p", { class: "fa-muted" }, "Submitted forms are locked; corrections are made as audited amendments. Queued until there's signal."),
  );
}

async function submit(f: LocalForm): Promise<void> {
  if (!confirm("Submit this form? It locks: later corrections become audited amendments.")) return;
  const content = hashableContent(f);
  const fp = await fingerprint(f, content);
  // each signature checked under its own scheme (row #84): only the ones that no longer cover the form go
  const stale = fp.h1 ? invalidRoles(f.signatures.filter((s) => s.content_hash), fp) : [];
  if (stale.length) {
    const labels = roleLabels(sigDefs(f), stale);
    showToast(`The form changed since it was signed - sign again: ${labels.join(", ")}.`);
    clearRoles(f, stale, labels, latestSigned(f.history));
    return;
  }
  const scheme = signSchemeFor(f.signatures);
  const hash = scheme === 2 ? fp.h2 ?? null : fp.h1;
  if (M.saveTimer) window.clearTimeout(M.saveTimer);
  await discard(`msqa-draft-${f.id}`).catch(() => {});
  f.status = "queued";
  f.error = null;
  await putLocal(f);
  await enqueue({
    client_uuid: `msqa-submit-${f.id}`, endpoint: `/api/msqa/forms/${encodeURIComponent(f.id)}/submit`, method: "POST",
    json: { project_id: f.project_id, template_key: f.template.key, template_version: f.template.version, content,
            signatures: f.signatures, client_hash: hash, hash_scheme: scheme, ...(f.recipients ? { recipients: f.recipients } : {}) },
    meta: { form_kind: "msqa_form", label: `MSQA ${f.template.short_title ?? f.template.title} (submit)` }, photos: [], photo_headings: [],
  });
  showToast(navigator.onLine ? "Submitting… (photos upload first)" : "Saved - it will submit when you have signal.");
  M.emailAfter.add(f.id);
  closeEditor();
}

function buildEditor(f: LocalForm): HTMLElement {
  const t = f.template;
  const c = f.content;
  saveNoteEl = h("small", { class: "fa-muted msqa-savenote" }, "Saved on this phone");
  sigHost = h("div", { class: "msqa-sigs" });
  submitHost = h("div", { class: "msqa-submit" });
  const nav = h("nav", { class: "msqa-nav" },
    h("a", { href: "#msqa-header" }, "Header"),
    ...t.sections.map((s) => h("a", { href: `#msqa-sec-${s.key}` }, s.letter ?? s.key)),
    h("a", { href: "#msqa-summary" }, "Sign"));
  nav.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest("a");
    if (!a) return;
    e.preventDefault();
    document.getElementById(a.getAttribute("href")!.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
  const readOnly = f.status === "queued" || f.status === "submitted";
  const root = h("div", { class: `msqa-editor${readOnly ? " ro" : ""}` },
    h("div", { class: "msqa-top" },
      h("button", { type: "button", class: "fa-link", onclick: () => closeEditor() }, "‹ Forms"),
      h("div", { class: "msqa-top-title" }, h("strong", {}, t.short_title ?? t.title), h("small", {}, f.project_name)),
      saveNoteEl,
      readOnly ? null : h("button", { type: "button", class: "fa-btn msqa-histbtn", onclick: () => void openHistory(f) }, "History")),
    nav,
    readOnly ? h("p", { class: "fa-warn" }, f.status === "queued" ? "Waiting to submit (queued on this phone)." : "Submitted - locked.") : null,
    readOnly ? null : emailBox({ formId: f.id, projectId: f.project_id, mode: "draft", recipients: f.recipients,
                                 onChange: (list) => { f.recipients = list; scheduleSave(f); } }),
    t.use_note ? h("details", { class: "msqa-use" }, h("summary", {}, "Use of form"), h("p", {}, t.use_note)) : null,
    h("section", { class: "msqa-block", id: "msqa-header" }, h("h3", {}, t.title), ...(t.header ?? []).map((d) => fieldEl(d, c.header, f)),
      t.intro_title ? h("strong", {}, t.intro_title) : null,
      ...(typeof t.intro === "string" ? [t.intro] : t.intro ?? []).map((p) => h("p", { class: "fa-muted msqa-intro" }, p))),
    ...(t.resources ?? []).map((g) => {
      const src = c.resources[g.key] ?? (c.resources[g.key] = {});
      return h("section", { class: "msqa-block msqa-res" }, h("h3", {}, g.label),
        h("div", { class: "msqa-grid" }, ...g.fields.filter((d) => d.type !== "items").map((d) => fieldEl({ ...d, label: d.label || d.placeholder || "Other" }, src as Record<string, string>, f))),
        ...g.fields.filter((d) => d.type === "items").map((d) => itemsEl(d, src, f)));
    }),
    ...t.sections.flatMap((s) => {
      const items = h("div", { class: "msqa-items" });
      const drawItems = () => {
        const on = sectionApplies(s, c);
        fill(items, ...(on ? s.items.map((it) => itemCard(f, s, it))
          : [h("p", { class: "fa-muted msqa-na" }, `Not applicable: the ${s.items.length} item(s) in this section are N/A and won't be sent. Tap Yes to answer them.`)]));
        if (readOnly) items.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement>("input, textarea, button, select").forEach((el) => { el.disabled = true; });
      };
      drawItems();
      return [
        h("h2", { class: "msqa-sec", id: `msqa-sec-${s.key}` }, `${s.letter ? `${s.letter}  ` : ""}${s.title ?? s.table_title ?? s.key}`,
          s.hold_point && !/HOLD POINT/i.test(s.title ?? "") ? h("span", { class: "msqa-hold" }, "HOLD POINT") : null),
        ...(s.pre_fields ?? []).map((d) => h("section", { class: "msqa-block" },
          fieldEl(d, c.pre, f, d.applicable_for ? () => { drawItems(); updateNcNote(f); } : undefined))),
        items,
      ];
    }),
    summaryBlock(f),
    sigHost,
    submitHost,
  );
  renderSignatures();
  const back = M.resumed;
  if (back && !readOnly) {
    M.resumed = null;
    const card = back.item ? root.querySelector<HTMLElement>(`#msqa-${CSS.escape(back.item)}`) : null;
    const note = h("p", { class: "fa-warn msqa-restore", role: "status" }, back.photo_pending
      ? `WRAP restarted while the camera was open, so that photo didn't reach ${back.item ?? "the form"}. Take it again, or use "From library" if the camera saved it. Everything else on the form was kept.`
      : "WRAP restarted - your form was reopened where you left it.");
    (card ?? root.querySelector(".msqa-top"))?.before(note);
    if (card) window.setTimeout(() => note.scrollIntoView({ block: "start" }), 60);
  }
  if (readOnly) root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement>("input, textarea, .msqa-sbtn, .msqa-segbtn, .msqa-sig button, .msqa-submit button, .fa-x, .msqa-now, .msqa-item-x, .msqa-item-add")
    .forEach((el) => { el.disabled = true; });
  return root;
}

function summaryBlock(f: LocalForm): HTMLElement {
  const t = f.template;
  const s = f.content.summary;
  const box = h("section", { class: "msqa-block", id: "msqa-summary" });
  const draw = () => {
    const sd = t.summary;
    ncNoteEl = h("p", { class: "fa-muted" });
    updateNcNote(f);
    fill(box,
      h("h3", {}, sd?.title ?? "Summary"),
      ncNoteEl,
      sd?.outcome ? h("div", { class: "msqa-radio" }, ...sd.outcome.options.map((o) => h("button", {
        type: "button", class: `msqa-opt${s.outcome === o.key ? " on" : ""}${o.tone ? ` tone-${o.tone}` : ""}${o.suspension ? " susp" : ""}`,
        onclick: () => {
          s.outcome = o.key;
          if (o.key !== "non_compliant") delete s.non_compliant_action;
          edited(f);
          draw();
          if (o.suspension) showToast("This outcome holds work: the project manager and delivery manager get an URGENT notification when it is submitted.");
        },
      }, o.label))) : null,
      sd?.non_compliant_action && s.outcome === "non_compliant" ? h("div", { class: "msqa-radio sub" }, ...sd.non_compliant_action.options.map((o) => h("button", {
        type: "button", class: `msqa-opt${s.non_compliant_action === o.key ? " on" : ""}${o.suspension ? " susp" : ""}`,
        onclick: () => {
          s.non_compliant_action = o.key;
          edited(f);
          draw();
          if (o.suspension) showToast("Suspension of work: the project manager and delivery manager get an URGENT notification when this is submitted.");
        },
      }, o.label))) : null,
      ...(sd?.fields ?? []).map((d) => fieldEl(d, s, f)),
      t.comments ? fieldEl({ key: t.comments.key, label: t.comments.label, type: "textarea" }, s, f) : null,
      ...(sd?.fields ?? []).some((d) => d.creates_actions) ? [h("small", { class: "fa-muted" }, "Each line of the conditions / actions box becomes a tracked action.")] : [],
      ...(sd?.statements ?? []).map((p) => h("p", { class: "fa-muted msqa-disclaimer" }, p)),
      sd?.disclaimer_title ? h("strong", { class: "msqa-disclaimer" }, sd.disclaimer_title) : null,
      sd?.disclaimer ? h("p", { class: "fa-muted msqa-disclaimer" }, sd.disclaimer) : null,
    );
    renderSubmit();
  };
  draw();
  return box;
}

// ---------------------------------------------------------------------------
// History (row #83): earlier versions, Restore this version
// ---------------------------------------------------------------------------

interface ServerRev {
  seq: number;
  content_hash: string;
  saved_at: string;
  client_saved_at: string | null;
  user_name: string | null;
  source: string;
  restored_from_seq: number | null;
  signed: boolean;
  signature_roles: string[];
  content: Content;
}
interface HistRow {
  at: string;
  hash: string;
  content: Content;
  roles: string[];
  note: string | null;
  restore: () => Promise<void>;
}

function timeWords(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const same = d.toDateString() === new Date().toDateString();
  return same ? `Today ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : fmtDate(iso);
}

async function restoreFromServer(f: LocalForm, seq: number, at: string): Promise<void> {
  await keepVersion(f); // the form as it is now stays in this phone's history too
  if (M.saveTimer) {
    window.clearTimeout(M.saveTimer);
    M.saveTimer = null;
  }
  await discard(`msqa-draft-${f.id}`).catch(() => {}); // an older queued save must not land on top of the restore
  let res: { content: Content; content_hash: string; seq: number; signatures: LocalSig[] };
  try {
    res = await postJson(`/api/msqa/forms/${encodeURIComponent(f.id)}/revisions/${seq}/restore`, { client_saved_at: new Date().toISOString() });
  } catch (e) {
    scheduleSave(f, true);
    showToast(`Couldn't restore that version: ${(e as Error).message}`);
    return;
  }
  const sigs: LocalSig[] = (res.signatures ?? []).map((s) => ({
    role: s.role, image: s.image, name: s.name ?? undefined, company: s.company ?? undefined, signed_at: s.signed_at,
    lat: s.lat ?? undefined, lon: s.lon ?? undefined, accuracy_m: s.accuracy_m ?? undefined, location_status: s.location_status,
    user_agent: s.user_agent, content_hash: s.content_hash,
    ...(s.hash_scheme === 2 ? { hash_scheme: 2, signer_digest: s.signer_digest ?? null } : {}),
  }));
  f.content = { ...emptyContent(), ...clone(res.content) };
  const fp = await fingerprint(f);
  const ok = fp.h1 === res.content_hash && signaturesValidFor(sigs, fp);
  f.signatures = ok ? sigs : [];
  f.restoreMark = undefined; // the server recorded (and audited) this restore itself
  if (ok) settleCleared(f);
  await keepVersion(f);
  rebuildEditor();
  const when = timeWords(at).replace(/^Today /, "");
  showToast(ok ? `Restored the version from ${when} - ${sigLabels(sigDefs(f), f.signatures).join(", ")} signature${f.signatures.length === 1 ? "" : "s"} kept.`
    : sigs.length ? `Restored the version from ${when}, but its signatures don't match it on this phone, so they were not put back. Sign again.`
      : `Restored the version from ${when}.`);
}

async function openHistory(f: LocalForm): Promise<void> {
  M.signAgainToast?.remove(); // the submit area still says it; the toast would cover the list
  M.signAgainToast = null;
  const list = h("ol", { class: "msqa-hist" }, h("li", { class: "fa-muted" }, "Loading…"));
  const note = h("p", { class: "fa-muted" });
  const overlay = h("div", { class: "msqa-modal msqa-histmodal", role: "dialog", "aria-label": "Form history" });
  const close = () => overlay.remove();
  overlay.append(h("div", { class: "msqa-modal-box" },
    h("div", { class: "msqa-hist-head" }, h("strong", {}, "History"), h("button", { type: "button", class: "fa-link", onclick: close }, "Close")),
    note, list));
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  (document.getElementById("field-app") ?? document.body).append(overlay);

  const defs = sigDefs(f);
  const labelsOf = (roles: string[]) => sigLabels(defs, roles.map((role) => ({ role })));
  const nowHash = await contentHash(f.id, f.template.key, f.template.version, hashableContent(f));
  let rows: HistRow[];
  try {
    const r = await getJson<{ revisions: ServerRev[] }>(`/api/msqa/forms/${encodeURIComponent(f.id)}/revisions`);
    note.textContent = "Every save of this form is kept. Restore puts back that version's answers and its signatures, exactly.";
    rows = r.revisions.map((v) => ({
      at: v.client_saved_at || v.saved_at, hash: v.content_hash, content: v.content, roles: v.signature_roles,
      note: v.source === "restore" ? (v.restored_from_seq ? `Restored from version ${v.restored_from_seq}` : "Restored on the phone") : null,
      restore: () => restoreFromServer(f, v.seq, v.client_saved_at || v.saved_at),
    }));
  } catch {
    note.textContent = `No signal: these are this phone's last ${M.phoneKeep} versions.`;
    rows = [...(f.history ?? [])].reverse().map((v) => ({
      at: new Date(v.at).toISOString(), hash: v.hash, content: v.content, roles: v.signatures.map((s) => s.role), note: null,
      restore: async () => { await keepVersion(f); await putBack(f, v, "history"); },
    }));
  }
  if (!rows.length) {
    fill(list, h("li", { class: "fa-muted" }, "No earlier versions yet."));
    return;
  }
  // the newest version with the same content and the same signed-or-not as the form on this phone
  const current = rows.findIndex((r) => r.hash === nowHash && (r.roles.length > 0) === (f.signatures.length > 0));
  fill(list, ...rows.map((r, i) => {
    const older = rows[i + 1];
    const labels = labelsOf(r.roles);
    const isNow = i === (current >= 0 ? current : -1);
    const lines = older ? describeChanges(f.template, older.content, r.content) : [];
    const what = (older && !lines.length && signatureChange(labelsOf(older.roles), labels)) || summariseChanges(lines, !older);
    return h("li", { class: `msqa-hist-row${labels.length ? " signed" : ""}${isNow ? " now" : ""}` },
      h("div", { class: "msqa-hist-top" }, h("strong", {}, timeWords(r.at)), h("span", { class: `msqa-hist-sig${labels.length ? " on" : ""}` }, signedWords(labels))),
      h("p", { class: "msqa-hist-what" }, what),
      r.note ? h("small", { class: "fa-muted" }, r.note) : null,
      isNow ? h("small", { class: "msqa-hist-nowtag" }, "This is the form now")
        : h("button", { type: "button", class: "fa-btn msqa-hist-restore", onclick: async () => {
          const q = labels.length ? `Restore this version? Its answers and the ${labels.join(" and ")} signature${labels.length === 1 ? "" : "s"} come back exactly. The form as it is now stays in the history.`
            : "Restore this version? The form as it is now stays in the history.";
          if (!(await askModal(q, "Restore this version", "Cancel"))) return;
          close();
          await r.restore();
        } }, "Restore this version"));
  }));
}

// ---------------------------------------------------------------------------
// Approve to proceed (the monitor's extra visit) - after submit
// ---------------------------------------------------------------------------

async function approveToProceed(form: ServerForm): Promise<void> {
  const rel = (M.templates ?? []).find((t) => t.key === form.template_key)?.signatures?.find((s) => s.after_submit);
  const title = rel ? `${rel.label.replace(/:$/, "")}${rel.caption ? ` ${rel.caption}` : ""}` : "Monitor extra visit (approval to proceed)";
  // row #47: a time picker, not a free-text prompt
  const rect = await timeModal(`${form.suspension_reasons?.some((r) => r.startsWith("hold point")) ? "Hold point cleared" : "Hazard(s) / deficiencies rectified"} at`);
  if (rect === null) return;
  const image = await signatureModal(title, `${userName()} - approving the Contractor to proceed.`);
  if (!image) return;
  const pos = await getPosition();
  await enqueue({
    client_uuid: `msqa-approve-${form.id}`, endpoint: `/api/msqa/forms/${encodeURIComponent(form.id)}/approve-to-proceed`, method: "POST",
    json: { rectified_at: rect, signature: { image, signed_at: new Date().toISOString(), lat: pos.lat, lon: pos.lon, accuracy_m: pos.accuracy_m,
                                             location_status: pos.status, user_agent: navigator.userAgent.slice(0, 400) } },
    meta: { form_kind: "msqa_form", label: "MSQA approval to proceed" }, photos: [], photo_headings: [],
  });
  showToast("Approval to proceed queued.");
  window.setTimeout(() => { if (M.overviewFor) void loadOverview(M.overviewFor); }, 2500);
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

const OUTCOME: Record<string, [string, string]> = {
  no_deficiencies: ["No deficiencies", "ok"], rectified_immediately: ["Rectified immediately", "warn"],
  referred_to_engineer: ["Referred to the Engineer", "warn"], suspension: ["SUSPENSION OF WORK", "bad"],
  compliant: ["Compliant", "ok"], non_compliant: ["Non-compliant", "warn"], verified: ["Verified", "ok"],
};

export function viewMsqa(host: MsqaHost): HTMLElement {
  M.host = host;
  hookUploads();
  void ensureTemplates();
  if (M.editing) {
    if (!M.editorEl) M.editorEl = buildEditor(M.editing);
    return M.editorEl;
  }
  const pid = host.projectId();
  const projects = host.projects();
  const box = h("div", { class: "msqa-list" });
  void loadLocal().then(async () => {
    const items = await listOutbox();
    drawList(box, pid, projects, items);
  });
  if (pid && M.overviewFor !== pid) void loadOverview(pid);
  drawList(box, pid, projects, []);
  return box;
}

function familyPicker(options: { key: string; label: string }[], active: string): HTMLElement | null {
  if (!options.length) return null;
  return h("div", { class: "msqa-family", role: "radiogroup", "aria-label": "Form templates" },
    h("span", { class: "fa-muted" }, "Templates:"),
    ...options.map((o) => h("button", {
      type: "button", role: "radio", "aria-checked": String(o.key === active),
      class: o.key === active ? "fa-btn msqa-family-on" : "fa-btn",
      onclick: () => { M.family = o.key; M.host?.rerender(); },
    }, o.label)));
}

function drawList(box: HTMLElement, pid: string | null, projects: ReturnType<MsqaHost["projects"]>, outbox: OutboxItem[]): void {
  const proj = projects.find((p) => p.id === pid);
  const ov = M.overviewFor === pid ? M.overview : null;
  const local = M.local.filter((f) => f.project_id === pid && f.owner === outboxOwner() && f.status !== "submitted");
  const pending = new Map(outbox.filter((i) => i.client_uuid.startsWith("msqa-submit-")).map((i) => [i.client_uuid.slice(12), i]));
  // the project's own default when its overview is loaded (2026-10-28); a family picked here is kept
  const choice = familyChoice(M.templates ?? [], ov?.template_families ?? M.families, M.family);
  const templates = choice.shown;
  fill(box,
    h("h3", {}, "MSQA site forms"),
    !pid ? h("div", {},
      h("p", { class: "fa-muted" }, "Pick the project you're monitoring:"),
      h("ul", { class: "fa-list" }, ...projects.map((p) => h("li", { class: "fa-cand", onclick: () => { M.host?.selectProject(p.id); } },
        h("span", { class: "fa-dot", style: `background:${p.in_construction ? "#805ad5" : "#a0aec0"}` }),
        h("span", { class: "fa-cand-main" }, h("strong", {}, p.name), h("small", {}, `${p.role ? `Yours (${p.role}) · ` : ""}${p.phase_label ?? ""}`))))))
      : h("div", {},
        h("p", { class: "msqa-proj" }, h("strong", {}, proj?.name ?? ov?.project.name ?? pid),
          h("button", { type: "button", class: "fa-link", onclick: () => M.host?.selectProject("") }, "Change")),
        ...(ov?.suspensions ?? []).map((s) => {
          const why = ov?.forms.find((x) => x.id === s.form_id)?.suspension_reasons ?? [];
          return h("p", { class: "fa-warn" }, `⚠ Work ${why.length && why.every((r) => r.startsWith("hold point")) ? `held (${why.join("; ")})` : "suspended"} on ${fmtDate(s.raised_at)} by ${s.raised_by_name ?? "the monitor"} - until the monitor approves the Contractor to proceed.`);
        }),
        ov ? h("p", { class: "fa-muted" }, `Last visit: ${ov.last_visit ?? "none yet"}${ov.days_since_last_visit != null ? ` (${ov.days_since_last_visit} day(s) ago)` : ""} · ${ov.open_non_compliances.length} open non-compliance(s)`) : null,
        M.overviewError ? h("p", { class: "fa-muted" }, M.overviewError) : null,
        !templates.length ? h("p", { class: "fa-muted" }, M.loadingTemplates ? "Loading the forms…" : "The form templates aren't on this phone yet - open this tab once with signal.") : null,
        familyPicker(choice.options, choice.active),
        h("div", { class: "msqa-new" }, ...templates.map((t) => h("button", {
          type: "button", class: "fa-btn fa-big", onclick: () => void startForm(t, pid, proj?.name ?? ov?.project.name ?? pid),
        }, `New ${t.short_title ?? t.title}`))),
        local.length ? h("h3", {}, "On this phone") : null,
        h("ul", { class: "fa-list" }, ...local.map((f) => {
          const q = pending.get(f.id);
          const state = f.status === "queued" ? (q?.failed ? `Refused: ${q.last_error}` : q?.last_error ? `Waiting: ${q.last_error}` : "Queued to submit")
            : f.status === "rejected" ? `Refused: ${f.error ?? ""}` : `Draft · ${new Date(f.updated).toLocaleString()}`;
          return h("li", { class: "fa-cand" },
            h("span", { class: "fa-cand-main", onclick: () => openEditor(f) }, h("strong", {}, f.template.short_title ?? f.template.title),
              h("small", {}, `${f.content.header.date ?? f.content.header.date_of_audit ?? ""} · ${state}`)),
            q?.failed ? h("button", { type: "button", class: "fa-btn", onclick: () => void reopenRejected(f, q) }, "Fix") : null,
            f.status === "draft" ? h("button", { type: "button", class: "fa-link", onclick: () => void deleteLocal(f) }, "Delete") : null);
        })),
        ov ? h("h3", {}, "Submitted") : null,
        ov ? h("ul", { class: "fa-list" }, ...ov.forms.filter((x) => x.status === "submitted").map((x) => {
          const [ol, oc] = OUTCOME[x.outcome ?? ""] ?? [x.outcome_label ?? x.outcome ?? "", x.outcome_tone ?? ""];
          const li = h("li", { class: "fa-cand msqa-sub" },
            h("span", { class: "fa-cand-main" }, h("strong", {}, `${x.short_title} · ${x.visit_date ?? ""}`),
              h("small", {}, `${x.created_by_name ?? ""}${x.nc_count ? ` · ${x.nc_count} non-compliance(s)` : ""}${x.n_amendments ? ` · ${x.n_amendments} amendment(s)` : ""}`),
              h("span", { class: `msqa-pill ${oc}` }, ol)),
            h("a", { class: "fa-btn", href: `/api/msqa/forms/${encodeURIComponent(x.id)}/outputs/clean`, target: "_blank", rel: "noopener" }, "PDF"),
            h("button", { type: "button", class: "fa-btn", onclick: (e: Event) => togglePhotoAdd(x, (e.currentTarget as HTMLElement).closest("li")!) }, "Add photos"),
            h("button", { type: "button", class: "fa-btn", onclick: (e: Event) => toggleEmail(x, pid, (e.currentTarget as HTMLElement).closest("li")!) }, "Email"),
            x.suspension && ov.suspensions.some((s) => s.form_id === x.id)
              ? h("button", { type: "button", class: "fa-btn", onclick: () => void approveToProceed(x) },
                (M.templates ?? []).find((t) => t.key === x.template_key)?.signatures?.find((sg) => sg.after_submit)?.label.replace(/:$/, "") ?? "Approve to proceed") : null);
          // review #29: straight after this phone submits it, the form's email box is open below its row
          const auto = M.emailAfter.has(x.id);
          if (auto) {
            M.emailAfter.delete(x.id);
            M.emailOpen.add(x.id);
          }
          return M.emailOpen.has(x.id) ? [li, emailPanel(x, pid, true)] : [li];
        })) : null,
      ),
  );
}

/** Review #29: the email box under a submitted form's row. */
function emailPanel(x: ServerForm, pid: string, open: boolean): HTMLElement {
  return h("li", { class: "msqa-addpanel msqa-emailpanel", "data-form": x.id },
    emailBox({ formId: x.id, projectId: pid, mode: "submitted", open, title: `Email ${x.short_title} · ${x.visit_date ?? ""}` }));
}

function toggleEmail(x: ServerForm, pid: string, li: HTMLElement): void {
  const next = li.nextElementSibling;
  if (next?.classList.contains("msqa-emailpanel")) {
    next.remove();
    M.emailOpen.delete(x.id);
    return;
  }
  M.emailOpen.add(x.id);
  li.after(emailPanel(x, pid, true));
}

/** Row #8: photos added to a SUBMITTED form (on the phone or a desktop
 * browser) - an audited amendment, never an unlock (msqaPhotos.ts). */
function togglePhotoAdd(x: ServerForm, li: HTMLElement): void {
  const open = li.nextElementSibling?.classList.contains("msqa-addpanel") ? li.nextElementSibling : null;
  if (open) {
    open.remove();
    return;
  }
  const t = (M.templates ?? []).find((tt) => tt.key === x.template_key);
  const items = t ? t.sections.flatMap((s) => s.items) : [];
  const itemSel = h("select", { class: "fa-input" }, ...items.map((it) => h("option", { value: it.key }, `${it.key} ${it.label}`)));
  const files = h("input", { type: "file", accept: "image/*", multiple: true, class: "fa-input" });
  const caption = h("input", { type: "text", class: "fa-input", maxlength: 500, placeholder: "Caption (optional)" });
  const reason = h("textarea", { class: "fa-input", rows: 2, placeholder: "Reason (required), e.g. photos taken on site, added afterwards" });
  const note = h("small", { class: "fa-muted" });
  const go = h("button", { type: "button", class: "fa-btn fa-primary" }, "Add to the form");
  go.addEventListener("click", () => {
    go.disabled = true;
    void addPhotosToSubmitted({ formId: x.id, item: itemSel.value, files: Array.from(files.files ?? []), caption: caption.value,
                                reason: reason.value, onProgress: (m) => { note.textContent = m; } })
      .then((n) => {
        showToast(`${n} photo${n === 1 ? "" : "s"} added to ${itemSel.value} - recorded as an amendment; the PDF is updated.`);
        panel.remove();
        if (M.overviewFor) void loadOverview(M.overviewFor);
      })
      .catch((e: unknown) => {
        const d = (e as { detail?: unknown }).detail;
        showToast(typeof d === "string" ? d : navigator.onLine ? (e as Error).message : "Needs signal: adding photos to a submitted form isn't queued.");
        note.textContent = "";
        go.disabled = false;
      });
  });
  const panel = h("li", { class: "msqa-addpanel" },
    h("p", { class: "fa-muted" }, "Adds photos to the submitted form as an audited amendment (who, when, why). The signed original is kept; the PDFs are regenerated."),
    !items.length ? h("p", { class: "fa-warn" }, "The form templates aren't loaded yet - try again with signal.") : null,
    h("label", { class: "fa-field" }, h("span", {}, "Item"), itemSel),
    h("label", { class: "fa-field" }, h("span", {}, "Photos (camera or library)"), files),
    h("label", { class: "fa-field" }, h("span", {}, "Caption"), caption),
    h("label", { class: "fa-field" }, h("span", {}, "Reason *"), reason),
    go, note);
  li.after(panel);
}

async function deleteLocal(f: LocalForm): Promise<void> {
  if (!confirm("Delete this draft from the phone (and the server's copy of the draft)?")) return;
  await tx(MSQA_FORMS, "readwrite", (s) => s.delete(f.id));
  await discard(`msqa-draft-${f.id}`).catch(() => {});
  try {
    await fetch(`/api/msqa/forms/${encodeURIComponent(f.id)}`, { method: "DELETE", credentials: "same-origin" });
  } catch {
    /* offline: the server's draft (if any) stays until deleted on the desktop */
  }
  await loadLocal();
  M.host?.rerender();
}

async function reopenRejected(f: LocalForm, q: OutboxItem): Promise<void> {
  await discard(q.client_uuid).catch(() => {});
  f.status = "rejected";
  f.error = q.last_error;
  f.signatures = [];
  await putLocal(f);
  openEditor(f);
}

/** Field app -> MSQA: "is a form open?" (the view keeps its DOM). */
export function msqaEditing(): boolean {
  return !!M.editing;
}
